import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  PopoverBase,
  Theme,
  Typography,
  useTheme,
} from '@chronosphereio/chrono-ui';
import { useDebounce } from '@chronosphereio/shared';
import { useCallback, useMemo } from 'react';
import { ThresholdColorPicker } from './ThresholdColorPicker';

const ColorChip = ({ color, background }: { color?: string; background?: string }) => {
  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border.bold}`,
        borderRadius: '5px',
        height: '28px',
        width: '28px',
        color: color ?? theme.palette.text.primary,
        background,
        cursor: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
      data-testid="color-chip"
    >
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        abc
      </Typography>
    </Box>
  );
};

export interface Color {
  color?: string;
  background?: string;
}

interface ColorPreset extends Color {
  name: string;
}

const buildColorPresets = (theme: Theme) =>
  [
    {
      // This option is meant to pick up the fallback values for both color and background. These values will vary between light mode and dark mode.
      color: undefined,
      background: undefined,
      name: 'Default',
    },
    {
      // This option is meant to pick up the fallback color for the background, which will vary between light mode and dark mode.
      color: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.success.main
        : theme.palette.designSystem.green[600],
      background: undefined,
      name: 'Green text',
    },
    {
      // This option is meant to pick up the fallback color for the background, which will vary between light mode and dark mode.
      color: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.warning.main
        : theme.palette.designSystem.orange[600],
      background: undefined,
      name: 'Orange text',
    },
    {
      // This option is meant to pick up the fallback color for the background, which will vary between light mode and dark mode.
      color: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.error.main
        : theme.palette.designSystem.red[600],
      background: undefined,
      name: 'Red text',
    },
    {
      // This option is meant to pick up the fallback color for the background, which will vary between light mode and dark mode.
      color: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.info.main
        : theme.palette.designSystem.blue[600],
      background: undefined,
      name: 'Blue text',
    },
    {
      color: 'white',
      background: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.success.main
        : theme.palette.designSystem.green[600],
      name: 'Green background',
    },
    {
      color: 'white',
      background: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.warning.main
        : theme.palette.designSystem.orange[600],
      name: 'Orange background',
    },
    {
      color: 'white',
      background: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.error.main
        : theme.palette.designSystem.red[600],
      name: 'Red background',
    },
    {
      color: 'white',
      background: theme.featureFlags.panwRebrandStubYear
        ? theme.palette.status.info.main
        : theme.palette.designSystem.blue[600],
      name: 'Blue background',
    },
  ] as ColorPreset[];

export const TextAndBackgroundColorPickerList = ({
  color: currentColor,
  onColorChange,
}: {
  color?: Color;
  onColorChange: (color: Color) => void;
}) => {
  const { theme } = useTheme();
  const presets = useMemo(() => buildColorPresets(theme), [theme]);

  // if color or background is undefined, these are the colors we will use
  const defaultBackground = theme.palette.background.container;
  const defaultText = theme.palette.text.primary;

  const color = {
    color: currentColor?.color || defaultText,
    background: currentColor?.background || defaultBackground,
  };

  const colorPickerPalette = [
    defaultText,
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.success.main : theme.palette.designSystem.green[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.warning.main : theme.palette.designSystem.orange[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.error.main : theme.palette.designSystem.red[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.info.main : theme.palette.designSystem.blue[600],
  ];

  const backgroundPickerPalette = [
    defaultBackground,
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.success.main : theme.palette.designSystem.green[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.warning.main : theme.palette.designSystem.orange[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.error.main : theme.palette.designSystem.red[600],
    theme.featureFlags.panwRebrandStubYear ? theme.palette.status.info.main : theme.palette.designSystem.blue[600],
  ];

  const handlePresetSelection = useCallback(
    (preset: ColorPreset) => {
      const { name: _, ...newColor } = preset;
      const color = { ...newColor };
      if (color.color === defaultText) {
        delete color.color;
      }

      if (color.background === defaultBackground) {
        delete color.background;
      }

      onColorChange(color);
    },
    [onColorChange, defaultBackground, defaultText]
  );

  const handleChange = useDebounce((newColor: Color) => {
    const defaultedColor: Color = { ...color, ...newColor };
    if (defaultedColor.color === defaultText) {
      delete defaultedColor.color;
    }

    if (defaultedColor.background === defaultBackground) {
      delete defaultedColor.background;
    }

    onColorChange(defaultedColor);
  }, 200);

  const handleTextColorChange = useCallback(
    (newTextColor: string) => {
      handleChange({ color: newTextColor === defaultText ? undefined : newTextColor });
    },
    [handleChange, defaultText]
  );

  const handleBackgroundColorChange = useCallback(
    (newBackgroundColor: string) => {
      handleChange({ background: newBackgroundColor === defaultBackground ? undefined : newBackgroundColor });
    },
    [handleChange, defaultBackground]
  );

  return (
    <Box width="250px">
      <List>
        {presets.map((preset, index) => (
          <ListItem key={index} dense disableGutters>
            <ListItemButton onClick={() => handlePresetSelection(preset)}>
              <ListItemIcon>
                <ColorChip color={preset.color} background={preset.background} />
              </ListItemIcon>
              <ListItemText>
                <Typography>{preset.name}</Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      <List>
        <ListItem>
          <ListItemIcon>
            <ColorChip {...color} />
          </ListItemIcon>
          <ListItemText>
            <Typography>Custom</Typography>
          </ListItemText>
        </ListItem>

        <ListItem
          secondaryAction={
            <ThresholdColorPicker
              color={color.color}
              onColorChange={handleTextColorChange}
              label="text"
              customPalette={colorPickerPalette}
            />
          }
        >
          <ListItemText>Custom text color</ListItemText>
        </ListItem>

        <ListItem
          secondaryAction={
            <ThresholdColorPicker
              color={color.background}
              onColorChange={handleBackgroundColorChange}
              label="background"
              customPalette={backgroundPickerPalette}
            />
          }
        >
          <ListItemText>Custom background color</ListItemText>
        </ListItem>
      </List>
    </Box>
  );
};

export const TextAndBackgroundColorPicker = ({
  color,
  onColorChange,
}: {
  color: Color;
  onColorChange: (color: Color) => void;
}) => {
  return (
    <PopoverBase
      data-testid="threshold color picker"
      Content={<TextAndBackgroundColorPickerList color={color} onColorChange={onColorChange} />}
      placement="right-start"
    >
      <IconButton>
        <ColorChip color={color.color} background={color.background} />
      </IconButton>
    </PopoverBase>
  );
};
